import { Vector3, Vector4 } from "three";
import { CursorDisk, GeometryUtils, isNull, SimplePointCloud } from "../../infra3d";
import { USERINPUTTYPE } from "../userinput/internal";
import { Tool } from "./internal";
/**
 * 3D Cursor for PointCloud Viewing
 * @class Cursor3d
 * @extends {Tool}
 */
export class Cursor3d extends Tool {
    constructor(scene, toolhandler, options, name, group) {
        super(scene, toolhandler);
        /** @property {MeasurePoint[]} */
        this.coordinates = [];
        // event variables
        /** @property {?number} mousemoveeventnr */
        this.mousemoveeventnr = null;
        /** @property {string} currentcursor*/
        this.currentcursor = "";
        this.actualPointCloud = null;
        this.refpos = null;
        this.mx = 0;
        this.my = 0;
        this.ctrlkeydownevtnr = -1;
        this.ctrlkeyupevtnr = -1;
        this.altkeydownevtnr = -1;
        this.pcrendered = false;
        this.scale = -1; //zoom dependend disk scale
        this.name = name;
        this.group = group;
        //options - set the default values here....
        const defaultoptions = {
            enablecbf: null,
            disablecbf: null,
            maxdist: 20,
            quality: true,
            pointcloudbydefault: false,
        };
        for (const property in defaultoptions) {
            this.options[property] = defaultoptions[property];
        }
        this.options.singleactive = false;
        super.parseOptions(options);
    }
    /**
     * internal function - to activate tool call mmSelectFeatureTool.enable
     * @method _enableTool
     * @protected
     */
    _enableTool() {
        this._subscribeEvents();
        this.fire(Cursor3d.enabled, this.options["cbobject"]);
        this.currentcursor = this.scene.getCursorStyleHandler().getCursorStyle();
        this.disk = new CursorDisk([0, 0, 0], 0.3);
        this.scene.infrascene.add(this.disk);
    }
    _subscribeEvents() {
        this.mousemoveeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEMOVE, this._onMouseMove, this);
        this.ctrlkeydownevtnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_CTRL, this._onCtrlDown, this);
        this.ctrlkeyupevtnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_CTRL_UP, this._onCtrlUp, this);
        this.altkeydownevtnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.KEY_ENTER, this._onEnterDown, this);
        if (this.options.enablecbf != null) {
            this.on(Cursor3d.enabled, this.options.enablecbf);
        }
        if (this.options.enablecbf != null) {
            this.on(Cursor3d.disabled, this.options.disablecbf);
        }
    }
    _unSubscribeEvents() {
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEMOVE, this.mousemoveeventnr);
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_CTRL, this.ctrlkeydownevtnr);
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_CTRL_UP, this.ctrlkeyupevtnr);
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.KEY_ENTER, this.altkeydownevtnr);
        if (this.options.enablecbf != null) {
            this.off(Cursor3d.enabled, this.options.enablecbf);
        }
        if (this.options.enablecbf != null) {
            this.off(Cursor3d.disabled, this.options.disablecbf);
        }
    }
    /**
     * internal function - to disable tool call mmSelectFeatureTool.disable
     * @method _disableTool
     * @protected
     */
    _disableTool() {
        // if tool is not active there is nothing to disable...
        if (!this.enabled)
            return;
        if (this.actualPointCloud)
            this.scene.infrascene.remove(this.actualPointCloud);
        if (this.disk != null) {
            this.scene.infrascene.remove(this.disk);
            this.scene.infrascene.render();
        }
        this.scene.getCursorStyleHandler().resetCursorStyle();
        this.fire(Cursor3d.disabled, this.options["cbobject"]);
        this._unSubscribeEvents();
    }
    /**
     * internal function - onmouse move the pc-view will be updated
     * @method _onMouseMove
     * @param evt
     * @private
     */
    _onMouseMove(evt) {
        this.mx = evt.offsetX;
        this.my = evt.offsetY;
        this.refpos = this.scene
            .getMeasureHandler()
            .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), this.mx, this.my, this.options.maxdist);
        if (this.refpos.warning) {
            this.disk.iMesh.visible = false;
            this.scene.getCursorStyleHandler().setCursorStyle("not-allowed");
        }
        else {
            this.disk.iMesh.visible = true;
            this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
        }
        this.disk.coordinates = [this.refpos.Easting, this.refpos.Northing, this.refpos.Height];
        this.scale = 1 - this.scene.infrascene.traversalState.zoom / 10;
        this.scale = Math.max(this.scale, 0.2);
        const w = Math.round((1 / this.scale) * 10);
        const thresh = 1;
        const surroundingcoord = [];
        for (let i = this.mx - w; i < this.mx + w; i += w / 5) {
            for (let j = this.my - w; j < this.my + w; j += w / 5) {
                const pos = this.scene
                    .getMeasureHandler()
                    .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), i, j, this.options.maxdist);
                if (GeometryUtils.CalcDistance(this.refpos, pos) < thresh && !pos.warning) {
                    //only green coordinates
                    surroundingcoord.push(new Vector3(pos.Easting, pos.Northing, pos.Height));
                }
            }
        }
        if (this.options.quality) {
            if (this.refpos.quality > 191) {
                this.disk.changeColor([0, 1, 0]);
            }
            else if (this.refpos.quality > 127) {
                this.disk.changeColor([1, 1, 0]);
            }
            else if (this.refpos.quality > 63) {
                this.disk.changeColor([1, 0, 0]);
            }
        }
        else {
            this.disk.changeColor([0.8, 0.8, 0.8]);
        }
        this.disk.iMesh.scale.set(this.scale, this.scale, this.scale);
        if (surroundingcoord.length > 0) {
            const plane = GeometryUtils.FitPlane(surroundingcoord);
            if (!isNull(plane)) {
                // Rounding to snap the orientation to values of 0, 0.33, 0.66, 1
                const normal = plane.plane.normal.clone();
                normal.x = Math.round(normal.x * 3) / 3;
                normal.y = Math.round(normal.y * 3) / 3;
                normal.z = Math.round(normal.z * 3) / 3;
                // As the scene is rotated this rotation needs to be applied to the normal vector
                const normalWorld = normal.clone();
                if (this.disk.iMesh.parent) {
                    this.disk.iMesh.parent.localToWorld(normalWorld);
                }
                const worldPosition = new Vector3();
                this.disk.iMesh.getWorldPosition(worldPosition);
                const targetPoint = worldPosition.clone().add(normalWorld);
                this.disk.iMesh.lookAt(targetPoint);
            }
        }
        this.disk.iMesh.updateMatrix();
        this.disk.iMesh.updateMatrixWorld();
        this.disk.render(this.scene.infrascene.traversalState);
        if ((evt.ctrlKey && !this.options.pointcloudbydefault) || (!evt.ctrlKey && this.options.pointcloudbydefault)) {
            this._renderPointCloud();
            this.pcrendered = true;
        }
        else {
            if (this.actualPointCloud) {
                this.scene.infrascene.remove(this.actualPointCloud);
            }
        }
        this.scene.infrascene.render();
    }
    _renderPointCloud() {
        this.disk.iMesh.visible = false;
        let color = [];
        const coorsarray = [];
        const cpos = this.scene
            .getMeasureHandler()
            .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), this.mx, this.my, this.options.maxdist);
        for (let i = 0; i < this.disk.screencoord.length; i++) {
            const pos = this.scene
                .getMeasureHandler()
                .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), Math.round(this.disk.screencoord[i].x), Math.round(this.disk.screencoord[i].y), this.options.maxdist);
            if (this.options.quality) {
                if (pos.quality > 191) {
                    color = [0, 0.8, 0];
                }
                else if (pos.quality > 127) {
                    color = [0.8, 0.8, 0];
                }
                else if (pos.quality > 63) {
                    color = [0.8, 0, 0];
                }
            }
            else {
                color = [0.8, 0.8, 0.8];
            }
            if (!pos.warning && GeometryUtils.CalcDistance(cpos, pos) < 0.4 * this.scale) {
                coorsarray.push([pos.Easting, pos.Northing, pos.Height, color[0], color[1], color[2]]);
            }
        }
        if (this.actualPointCloud) {
            this.scene.infrascene.remove(this.actualPointCloud);
        }
        if (coorsarray.length > 0) {
            this.actualPointCloud = new SimplePointCloud(coorsarray);
            this.scene.infrascene.add(this.actualPointCloud);
        }
    }
    _onCtrlDown(evt) {
        if (!this.pcrendered) {
            this._renderPointCloud();
            this.pcrendered = true;
        }
    }
    _onCtrlUp(evt) {
        this.disk.iMesh.visible = true;
        if (this.actualPointCloud)
            this.scene.infrascene.remove(this.actualPointCloud);
        this.pcrendered = false;
    }
    /**
     * removes all drawing from the scene
     * @method _cleanUp
     * @private
     */
    _cleanUp() {
        if (this.coordinates.length > 0)
            this.coordinates = [];
    }
    /**
     * Secret hidden feature -> write all actual visible points to console...
     * @private
     */
    _onEnterDown(evt) {
        if (evt.altKey) {
            const surroundingcoord = [];
            console.log("Extract Pointcloud... hold on, this takes a while...");
            for (let i = 0; i < this.scene.engine.getContextWidth(); i++) {
                for (let j = 0; j < this.scene.engine.getContextHeight(); j++) {
                    const pos = this.scene
                        .getMeasureHandler()
                        .createMonoplotMeasurePoint(this.scene.getCurrentGeoframe(), i, j, this.options.maxdist);
                    if (pos.quality != 192)
                        continue;
                    if (GeometryUtils.CalcDistance(this.refpos, pos) < 10 && !pos.warning) {
                        surroundingcoord.push(new Vector4(pos.Easting, pos.Northing, pos.Height, pos.quality));
                    }
                }
                if (i % 50 == 0) {
                    console.log("I'm still working... this was line number:  " +
                        i +
                        " of " +
                        this.scene.engine.getContextWidth());
                }
            }
            let s = "";
            for (let i = 0; i < surroundingcoord.length; i++) {
                s += surroundingcoord[i].x + "," + surroundingcoord[i].y + "," + surroundingcoord[i].z + "\n";
            }
            const blob = new Blob([s]);
            const a = window.document.createElement("a");
            a.href = window.URL.createObjectURL(blob);
            a.download = "pointcloud.xyz";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    }
}
Cursor3d.enabled = "enabled";
Cursor3d.disabled = "disabled";
//# sourceMappingURL=cursor3d.js.map